"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Users,
  Sparkles,
  Stethoscope,
  Crown,
  Brain,
  Mic,
  ArrowRight,
  Heart,
  Lock,
  Layers,
  Compass,
  Award,
  Activity,
  Briefcase,
  Target,
  Lightbulb,
  Building2,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TimelineBlock } from "@/lib/blocks/timeline-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const DOMAINS = [
  {
    icon: Stethoscope,
    title: "The Clinical Method for Leadership",
    body: "The physician&apos;s discipline — diagnosis, triage, protocol, judgment, outcome accountability — applied to the mentee&apos;s specific leadership challenges. Each session begins with a diagnostic assessment, followed by evidence-based intervention design, protocol development, and outcome measurement. Not a metaphor. The actual operating system of a physician, transmitted to the mentee&apos;s organizational reality.",
    accent: "red",
  },
  {
    icon: Activity,
    title: "Operational Excellence Principles",
    body: "Protocols that prevent failure, checklists that ensure consistency, decision frameworks that produce reliable outcomes, accountability structures that sustain execution over time. The operating method translated into the mentee&apos;s organizational context — the same discipline that produces verified outcomes at sovereign scale, internalized as the mentee&apos;s own leadership practice.",
    accent: "purple",
  },
  {
    icon: Heart,
    title: "The 70% Operating Model",
    body: "Building and leading high-performance diverse teams — the operational evidence, the implementation method, the measurable outcomes. The mentee learns not just why diversity produces better results but exactly how to build teams that achieve it. A decade of verified data proving that teams composed of over 70% women produce the most reliable outcomes, transmitted as implementable practice.",
    accent: "emerald",
  },
  {
    icon: Lock,
    title: "The Discretion Doctrine",
    body: "Operating at high levels with confidentiality and integrity — the security protocols, the compartmentalization methods, the discipline of operating in environments where information leakage has national consequences. The same operational discipline that has produced zero security incidents across fifteen-plus years, transferred as a leadership capability.",
    accent: "cyan",
  },
  {
    icon: Layers,
    title: "Building Things That Last",
    body: "The long-term discipline of operational leadership — how to build organizations that sustain excellence beyond the founder&apos;s direct involvement, how to embed operating methods into organizational culture, how to create systems that produce consistent outcomes without constant oversight. The ultimate test of leadership.",
    accent: "amber-gold",
  },
];

const MENTEES = [
  {
    icon: Crown,
    title: "Executives Preparing for C-Suite",
    body: "Senior leaders transitioning into the highest levels of organizational leadership — the transition that demands not just experience but a systematic method for navigating sovereign-scale complexity. The operating system for C-suite performance: diagnostic precision, triage discipline, protocol-driven execution, outcome accountability.",
    accent: "red",
  },
  {
    icon: Briefcase,
    title: "Founders Scaling Beyond Startup",
    body: "Founders who have built successful organizations and now face the operational complexity of scaling — from founder-led to professionally operated, from single-market to multi-national, from startup discipline to enterprise rigor. The specific challenges of this transition: embedding operating methods, building systems, sustaining excellence.",
    accent: "purple",
  },
  {
    icon: Heart,
    title: "Women Leaders Navigating Male-Dominated Environments",
    body: "Women leaders who need both the operating method and the evidence that women lead effectively at sovereign scale. The 70% Operating Model — a decade of verified data — along with the clinical discipline, the discretion doctrine, and the operational infrastructure that enable women to lead at the highest levels. Operational superiority, not encouragement.",
    accent: "emerald",
  },
  {
    icon: Target,
    title: "Emerging Leaders Facing Unprecedented Complexity",
    body: "Leaders confronting operational complexity they have not encountered before — the kind that requires not just experience but a systematic method for navigating it. Diagnostic frameworks, triage methodologies, decision frameworks under uncertainty, accountability structures that sustain performance across extended timelines.",
    accent: "cyan",
  },
];

const APPROACH = [
  { num: "01", title: "Diagnose", body: "Every session begins with a diagnostic assessment — the clinical method applied to the mentee&apos;s current operational landscape. The diagnostic maps the leadership context, the operational challenges, the existing capabilities, the gaps, the root causes, the systemic patterns. The diagnosis identifies not just challenges but structural vulnerabilities." },
  { num: "02", title: "Prescribe", body: "Following the diagnosis, the prescription specifies the specific operating mechanisms the mentee will deploy — the protocols, checklists, decision frameworks, and accountability structures. The prescription is grounded in the diagnostic data, not in a predetermined development program. Each prescription element is implementable and measurable." },
  { num: "03", title: "Implement", body: "The mentee deploys the prescribed mechanisms within their organizational context. The implementation is supported through structured session engagement — guidance, refinement, troubleshooting, and adaptation as the operating method is applied to real challenges. The implementation is the test of the prescription." },
  { num: "04", title: "Measure", body: "Outcomes are measured against the metrics established in the prescription — the same accountability that governs medical practice. The mentee&apos;s leadership capability, decision-making speed, execution reliability, and operational discipline are measured with clinical specificity. The measurement produces the data that informs the next diagnostic cycle." },
];

const GAINS = [
  { icon: Stethoscope, title: "Diagnostic Precision", body: "The ability to assess organizational challenges with the forensic precision of a physician — identifying not just symptoms but root causes, not just problems but the systemic patterns that created them.", accent: "red" },
  { icon: Activity, title: "Operational Discipline", body: "Protocols, checklists, decision frameworks, and accountability structures internalized as the mentee&apos;s own leadership practice — operating mechanisms that produce consistent outcomes under pressure.", accent: "purple" },
  { icon: Brain, title: "Cross-Domain Integration", body: "The Triple Threat perspective — medicine, technology, and politics as one operating method — internalized as the mentee&apos;s own cross-domain analytical capability, producing insights single-domain expertise cannot achieve.", accent: "cyan" },
  { icon: Lock, title: "Discretion Mastery", body: "The discipline of operating at sovereign levels with absolute confidentiality and integrity — the protocols, compartmentalization methods, and operational security that have produced zero incidents across fifteen-plus years.", accent: "emerald" },
  { icon: Heart, title: "The 70% Implementation Method", body: "How to build, lead, and sustain majority-female teams that outperform conventional compositions — the operational evidence, the recruitment strategies, the integration protocols, and the measurable outcomes.", accent: "amber-gold" },
  { icon: Layers, title: "Endurance Architecture", body: "The ability to build organizations that sustain excellence beyond the founder&apos;s direct involvement — the embedding methodology, the cultural transmission, and the systems that produce consistent outcomes without constant oversight.", accent: "denim" },
];

export default function ExecutiveMentorshipPage() {
  return (
    <>
      <PageHero
        icon={Users}
        overline="Service · Executive Mentorship"
        overlineClassName="text-emerald"
        title={
          <>
            One-on-One Leadership Development, <span className="text-gradient-premium">Transmitted from a Decade of Building.</span>
          </>
        }
        subtitle="Direct mentorship for executives, founders, women leaders, and emerging leaders preparing for sovereign-scale challenges. This is not coaching. This is the transfer of an operating method from someone who has built and run sovereign-scale operations to someone preparing to lead at that level."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Executive Mentorship" }]}
        badge="The operating method, transmitted personally"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-5`}>
              <Sparkles className="size-3.5 text-emerald" />
              <span className="text-overline text-emerald">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              The transfer of an operating system, not advice.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The mentorship addresses five core domains — the clinical method for leadership, operational excellence principles, the 70% Operating Model, the discretion doctrine, and building things that last. Each domain is grounded in verified operational experience, not theory. Each session produces measurable progress. Each month produces a demonstrable shift in the mentee&apos;s operational capability.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Organizations and leaders that engage for mentorship are not purchasing a development program. They are accessing an operating system — the same method that has driven sovereign-scale operations across eighteen countries, processed data for over nine hundred million citizens, and sustained zero security incidents across fifteen-plus years. The mentorship concludes not when the calendar runs out but when the operating method is embedded.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The mentorship is curated — available to leaders whose challenges demand the full weight of sovereign-scale operational expertise. The diagnostic conversation determines whether the engagement is appropriate, whether the mentee&apos;s needs align with capabilities, and whether the mentorship can produce the verified outcomes that both parties require.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { num: "01", title: "Diagnose Before Prescribing", body: "Every mentorship begins with understanding the mentee — the leadership context, the operational challenges, the existing capabilities, the gaps. No mentorship is prescribed without diagnosis.", accent: "red" },
              { num: "02", title: "Protocol Over Motivation", body: "Motivation fades. Protocols persist. The deliverable is an operating system — mechanisms that persist beyond the session itself and that the mentee deploys independently.", accent: "purple" },
              { num: "03", title: "Outcome Accountability", body: "Measured by outcomes, not effort. The same accountability that governs medical practice governs every mentorship engagement. Capability is measured. Progress is measured.", accent: "cyan" },
              { num: "04", title: "Discretion as Foundation", body: "National security level confidentiality, binding agreements, compartmentalized operations. The structural foundation of how every mentorship operates.", accent: "emerald" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 ring-2 ${a.ring}`}>
                      <span className="text-caption font-bold text-white">{p.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Target className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">What Mentorship Covers</span>
          </div>
          <h2 className="text-display-md mb-4">Five interconnected domains of operational leadership.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each domain is grounded in verified operational experience, each producing measurable capability development, each building on the others to form a complete operating system for sovereign-scale leadership.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOMAINS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <d.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{d.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <Lightbulb className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">The Approach</span>
          </div>
          <h2 className="text-display-md mb-4">Four stages of structured mentorship.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each session follows the same clinical discipline. The stages are sequential in logic but iterative in practice — the cycle produces compound capability development as the mentee&apos;s operational literacy deepens.
          </p>
        </div>
        <div className="space-y-3">
          {APPROACH.map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring} group-hover:scale-110 transition-all`}>
                    <span className="text-body-md font-bold text-white">{s.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.coral.pill} mb-4`}>
            <Crown className="size-3.5 text-coral" />
            <span className="text-overline text-coral">Ideal Mentees</span>
          </div>
          <h2 className="text-display-md mb-4">Who benefits from executive mentorship.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The mentorship is designed for leaders whose challenges demand sovereign-scale operational expertise — leaders who have moved beyond foundational development and require the transfer of an operating method tested across the most demanding environments on earth.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {MENTEES.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <m.icon className="size-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{m.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Award className="size-3.5 text-denim" />
            <span className="text-overline text-denim">What Mentees Gain</span>
          </div>
          <h2 className="text-display-md mb-4">Six capabilities that compound over time.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The mentorship produces embedded capability — the operating method internalized, not merely understood. Each capability compounds as the mentee applies it to increasingly complex challenges.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GAINS.map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <g.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{g.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{g.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Activity className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Mentorship Timeline</span>
          </div>
          <h2 className="text-display-md mb-4">Six milestones of capability development.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The mentorship produces capability development measured against six milestones. Each milestone represents a specific operational capability — the mentee&apos;s progression is tracked against the framework.
          </p>
        </div>
        <TimelineBlock
          orientation="horizontal"
          milestones={[
            { period: "Month 1-2", title: "Diagnostic Mastery", body: "Internalized clinical method: diagnosis before treatment becomes the mentee's default approach to organizational challenges. Triage discipline replaces reactive prioritization.", accent: "red" },
            { period: "Month 3-4", title: "Protocol Authoring", body: "Capability to design and implement operating protocols for the mentee's own organizational context. The mentee drafts protocols independently, with mentorship review and refinement.", accent: "purple" },
            { period: "Month 5-6", title: "Decision Framework Deployment", body: "Evidence-based decision frameworks internalized and deployed across the mentee's domain. Decision speed improves measurably. Decision quality verification through outcome tracking.", accent: "cyan" },
            { period: "Month 7-9", title: "Operational Architecture", body: "Full operational excellence system designed and implemented across the mentee's scope of responsibility. Systems thinking replaces ad hoc intervention. Protocols, checklists, and accountability structures operate as integrated infrastructure.", accent: "emerald" },
            { period: "Month 10-12", title: "70% Implementation", body: "High-performance diverse team construction. Recruitment strategies, integration protocols, and performance measurement systems designed and operationalized. The 70% Operating Model internalized as the mentee's own practice.", accent: "amber-gold" },
            { period: "Beyond", title: "Endurance Architecture", body: "The mentee can now build organizations that sustain excellence beyond their direct involvement. The operating method persists as institutional capability. The mentorship concludes when the operating method is embedded.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Brain className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Mentorship Capabilities</span>
          </div>
          <h2 className="text-display-md mb-4">What mentees are capable of after sustained engagement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The mentorship produces embedded capability — the operating method internalized, not merely understood. Each capability compounds as the mentee applies it to increasingly complex challenges.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Sovereign-Scale Decision Authority", body: "The ability to make consequential decisions with the diagnostic precision of a physician and the operational discipline of a sovereign-scale operator. The mentee operates from a position of authority grounded in evidence and method.", accent: "red" },
            { title: "Institutional Operating System Design", body: "Capability to design and implement complete operational systems — protocols, checklists, decision frameworks, accountability structures — that produce consistent outcomes across the mentee's scope of responsibility.", accent: "purple" },
            { title: "Crisis Navigation Under Sovereign Pressure", body: "The clinical discipline of triage, protocol, and judgment — applied under the kinds of pressure that sovereign-level operations routinely produce. The mentee maintains operational continuity when systems fail.", accent: "cyan" },
            { title: "Talent Operating Model Implementation", body: "How to build, lead, and sustain high-performance diverse teams — recruitment strategies, integration protocols, performance measurement, and the verifiable outcomes the 70% Operating Model produces.", accent: "emerald" },
            { title: "Sovereign-Grade Discretion Mastery", body: "Operating at the highest levels with absolute confidentiality and integrity. The protocols, compartmentalization methods, and operational security that have produced zero incidents across sovereign-scale engagements.", accent: "amber-gold" },
            { title: "Cross-Domain Strategic Perspective", body: "The Triple Threat perspective internalized — medicine, technology, and politics as one operating method. Strategic decisions consider operational, technological, political, and human dimensions simultaneously.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Lightbulb className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Questions leaders ask before engaging mentorship.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Mentorship is curated, not marketed. The questions below address what leaders most frequently ask before initiating a mentorship engagement.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "What is the typical mentorship duration?", a: "The mentorship typically runs six to twelve months for initial capability development, with ongoing retainer relationships for sustained leadership development. Duration is calibrated to the diagnostic data — the mentee's starting capability, the operational challenges, and the outcomes sought. The mentorship concludes when the operating method is embedded, not when the calendar runs out." },
            { q: "How are mentorship sessions structured?", a: "Sessions are typically scheduled weekly or biweekly, with session length of 60 to 90 minutes. Each session follows the clinical method: diagnostic assessment, prescription design, implementation review, and outcome measurement. The cadence adapts to the mentee's operational schedule and the engagement's stage." },
            { q: "Is the mentorship available virtually or in person?", a: "The mentorship is available in both formats. Virtual sessions maintain the same operational standards as in-person engagement. In-person sessions are available for mentees in compatible geographies. The format is selected based on the mentee's preference, operational schedule, and the engagement's requirements." },
            { q: "What does the mentorship cost?", a: "Mentorship fees are discussed during the diagnostic phase, tailored to the engagement duration, session frequency, customization requirements, and outcome scope. The diagnostic conversation determines scope and fee structure simultaneously. The mentorship is curated, not marketed." },
            { q: "Can the mentorship combine with other services?", a: "Yes. Mentorship frequently combines with executive advisory for leadership team development, corporate workshops for organizational capability, and operational consulting for organization-wide transformation. The combinations are designed around the diagnostic data and the outcomes the mentee and their organization seek." },
            { q: "How is mentorship success measured?", a: "Success is measured against the diagnostic benchmarks: decision-making speed, execution reliability, operational discipline, leadership capability. The mentee's progression is tracked through the six-milestone framework. The same accountability standard that governs medical practice governs every mentorship engagement." },
            { q: "What happens after the mentorship concludes?", a: "The mentorship produces embedded capability — the operating method internalized, not merely understood. The mentee operates independently with the operating method as their own practice. Optional follow-up sessions are available for sustained development. The relationship can extend through advisory engagements, board service, or speaking partnerships." },
            { q: "Is the mentorship available to leaders outside sovereign-scale operations?", a: "Yes. The mentorship is available to any leader whose challenges demand the operating method. Sovereign-scale experience is brought to every engagement regardless of the mentee's current scope. The diagnostic conversation determines alignment between the challenge and the operating method." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Users className="size-10 text-emerald mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Inquire about executive mentorship.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The mentorship is curated — available to leaders whose challenges demand the full weight of sovereign-scale operational expertise. The diagnostic conversation determines whether the engagement is appropriate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin Mentorship Inquiry
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/executive-advisory/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Briefcase className="size-4" />
                Executive Advisory
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Executive Advisory", description: "The operating method embedded in leadership teams and daily practice.", href: "/services/executive-advisory/", icon: Briefcase, accent: "purple" },
          { title: "Board Service", description: "Sovereign-grade governance oversight at the board level.", href: "/services/board-service/", icon: Building2, accent: "cyan" },
          { title: "Corporate Workshops", description: "Intensive team training on the operating method — systems, not slide decks.", href: "/services/corporate-workshops/", icon: Layers, accent: "lime" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "emerald" },
        ]}
      />
    </>
  );
}
